import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Icon } from '@/shared/icons';
import { Breadcrumb } from '@/shared/components/ui/Breadcrumb';
import { Button } from '@/shared/components/ui/Button';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import { PageToolbar } from '@/shared/components/ui/PageToolbar';
import { StatusPill } from '@/shared/components/ui/StatusPill';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { useAudience, useDeleteAudience } from '../hooks';
import { AudienceEntriesTable } from '../components/AudienceEntriesTable';
import { AddEntryModal } from '../components/AddEntryModal';
import { ImportCsvModal } from '../components/ImportCsvModal';
import { ImportProgressBadge } from '../components/ImportProgressBadge';
import {
  audienceStatusMeta,
  audienceTypeMeta,
  formatEntries,
} from '../components/audiencePresentation';

export const AudienceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: audience, isLoading, error } = useAudience(id);
  const deleteAudience = useDeleteAudience();
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const confirm = useConfirm();

  const handleDelete = async () => {
    if (!id) return;
    if (!(await confirm({ message: 'Tem certeza que deseja excluir este público? Todas as entradas serão removidas.', title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    deleteAudience.mutate(id, {
      onSuccess: () => {
        toast.success('Público excluído');
        navigate('/prospecting/audiences');
      },
      onError: (err) => toast.error(err.message || 'Erro ao excluir'),
    });
  };

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: 'var(--theme-page-background)' }}
      >
        <p style={{ color: 'var(--theme-muted-text)' }}>Carregando...</p>
      </div>
    );
  }

  if (error || !audience || !id) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: 'var(--theme-page-background)' }}
      >
        <div className="text-center">
          <p style={{ color: 'var(--theme-error-color)' }} className="mb-3">
            {error?.message ?? 'Público não encontrado'}
          </p>
          <Link
            to="/prospecting/audiences"
            className="text-sm hover:underline"
            style={{ color: 'var(--theme-primary)' }}
          >
            Voltar para a lista
          </Link>
        </div>
      </div>
    );
  }

  const isProcessing = audience.processingStatus === 'processing';
  const hasFailure =
    audience.processingStatus === 'failed' && audience.lastImport?.errorMessage;

  const type = audienceTypeMeta[audience.identifierType];
  const status = audienceStatusMeta[audience.processingStatus] ?? audienceStatusMeta.idle;

  // Iniciais do nome para o avatar sólido (bate com `.prof-av` do mockup)
  const initials =
    audience.name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase() || '?';

  const lastImportDate = audience.lastImport?.finishedAt
    ? new Date(audience.lastImport.finishedAt).toLocaleDateString('pt-BR')
    : null;
  const subLine = lastImportDate
    ? `${type.label} · última importação em ${lastImportDate}`
    : type.label;

  // Métricas: apenas valores reais do modelo. Total sempre; métricas de importação
  // quando há um último relatório (lastImport).
  const stats: { label: string; value: string }[] = [
    { label: 'Total de entradas', value: formatEntries(audience.totalEntries) },
  ];
  if (audience.lastImport) {
    stats.push({ label: 'Importadas (última)', value: formatEntries(audience.lastImport.imported) });
    stats.push({ label: 'Duplicadas ignoradas', value: formatEntries(audience.lastImport.duplicatesIgnored) });
    stats.push({ label: 'Inválidas rejeitadas', value: formatEntries(audience.lastImport.invalidRejected) });
  }

  const overflowActions: CardAction[] = [
    { label: 'Excluir público', icon: 'trash', onClick: handleDelete, variant: 'danger' },
  ];

  return (
    <div
      className="h-full flex flex-col overflow-hidden"
      style={{ backgroundColor: 'var(--theme-page-background)' }}
    >
      {/* Toolbar shared 3 zonas — token header, 56px (breadcrumb, nunca seta voltar, + ações) */}
      <PageToolbar
        data-testid="audience-detail-toolbar"
        left={
          /* F25-31: breadcrumb shared — hover muted→texto, legenda mobile */
          <Breadcrumb
            items={[
              {
                label: 'Prospecção',
                onNavigate: () => navigate('/prospecting/audiences'),
                testid: 'audience-detail-breadcrumb-prospecting',
              },
              {
                label: 'Públicos',
                onNavigate: () => navigate('/prospecting/audiences'),
                testid: 'audience-detail-breadcrumb-list',
              },
              { label: audience.name, testid: 'audience-detail-breadcrumb-current' },
            ]}
            mobileLegend="Prospecção · Público"
            data-testid="audience-detail-breadcrumb"
          />
        }
        right={
          <>
            <Button
              variant="secondary"
              icon="send"
              collapsibleLabel
              className="flex-none"
              onClick={() => navigate(`/prospecting/campaigns/new?audienceId=${id}`)}
              title="Usar em campanha"
              data-testid="audience-detail-use-in-campaign"
            >
              Usar em campanha
            </Button>
            <Button
              variant="secondary"
              icon="upload"
              collapsibleLabel
              className="flex-none"
              onClick={() => setShowImport(true)}
              disabled={isProcessing}
              title="Importar CSV"
              data-testid="audience-detail-import"
            >
              Importar CSV
            </Button>
            <Button
              variant="primary"
              icon="plus"
              collapsibleLabel
              className="flex-none"
              onClick={() => setShowAddEntry(true)}
              title="Adicionar"
              data-testid="audience-detail-add"
            >
              Adicionar
            </Button>
            <CardActionsMenu actions={overflowActions} data-testid="audience-detail-actions" />
          </>
        }
      />

      {/* Conteúdo */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6 space-y-4">
          {/* Cartão de identidade */}
          <div
            className="rounded-xl border p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              borderColor: 'var(--theme-card-border)',
            }}
          >
            <div
              className="w-16 h-16 flex items-center justify-center flex-shrink-0 font-bold text-[22px]"
              style={{
                borderRadius: '32%',
                backgroundColor: 'var(--theme-primary)',
                color: '#ffffff',
              }}
            >
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <h1
                className="text-xl font-bold truncate"
                style={{ color: 'var(--theme-card-text)', letterSpacing: '-0.02em' }}
              >
                {audience.name}
              </h1>
              <p className="text-sm mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
                {subLine}
              </p>
              <div className="mt-2.5 flex items-center flex-wrap gap-2">
                <StatusPill
                  variant={status.variant}
                  label={status.label}
                  data-testid={`audience-status-${audience.id}`}
                />
                <span
                  className="inline-flex items-center gap-1.5 h-[23px] px-2.5 rounded-full text-xs font-semibold"
                  style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-card-text)' }}
                >
                  <Icon name={type.icon} className="w-3 h-3" />
                  {type.label}
                </span>
              </div>
            </div>
          </div>

          {/* Descrição */}
          {audience.description && (
            <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
              {audience.description}
            </p>
          )}

          {/* Progresso de importação (socket) */}
          <ImportProgressBadge audienceId={id} initialVisible={isProcessing} />

          {/* Banner de erro da última importação */}
          {hasFailure && (
            <div
              className="rounded-lg border p-4 flex items-start gap-3"
              style={{
                backgroundColor: 'var(--theme-error-background)',
                borderColor: 'var(--theme-error-color)',
              }}
            >
              <Icon
                name="alert-circle"
                className="w-5 h-5 flex-shrink-0 mt-0.5"
                style={{ color: 'var(--theme-error-color)' }}
              />
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--theme-error-color)' }}>
                  Falha na última importação
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--theme-error-color)' }}>
                  {audience.lastImport?.errorMessage}
                </p>
              </div>
            </div>
          )}

          {/* Métricas */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-xl border p-3.5"
                style={{
                  backgroundColor: 'var(--theme-card-background)',
                  borderColor: 'var(--theme-card-border)',
                }}
              >
                <div
                  className="text-xl font-extrabold tabular-nums"
                  style={{ color: 'var(--theme-card-text)', letterSpacing: '-0.02em' }}
                >
                  {stat.value}
                </div>
                <div className="text-xs mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          {/* Entradas */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <h2 className="text-sm font-bold" style={{ color: 'var(--theme-card-text)' }}>
                Entradas
              </h2>
              <span className="text-sm font-medium" style={{ color: 'var(--theme-muted-text)' }}>
                · {formatEntries(audience.totalEntries)}
              </span>
            </div>
            <AudienceEntriesTable audienceId={id} />
          </div>
        </div>
      </div>

      <AddEntryModal
        isOpen={showAddEntry}
        onClose={() => setShowAddEntry(false)}
        audienceId={id}
        identifierType={audience.identifierType}
      />
      <ImportCsvModal
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        audienceId={id}
      />
    </div>
  );
};
